import { Alert, Skeleton } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { getPortalProjectConversation } from "../../Messages/api/services.ts"
import { ConversationView } from "../../Messages/components/ConversationView.tsx"
import { useOpenConversations } from "../../Messages/hooks/use-open-conversations.ts"
import { upsertConversation } from "../../Messages/redux/messaging.slice.ts"
import type { ProjectConversations } from "../../Messages/types/messaging.types.ts"

/**
 * The customer's conversation with their project manager.
 *
 * The *same* conversation record the GC app shows — this is the portal's view
 * of it, not a parallel thread. The customer reaches exactly one room here and
 * the portal is never told that the project's internal rooms exist.
 */
export function ProjectMessagesTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [loading, setLoading] = useState(true)
  const [setup, setSetup] = useState<
    ProjectConversations["customer_setup"] | null
  >(null)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [error, setError] = useState(false)

  const conversation = useAppSelector((state) =>
    state.messaging.conversations.find((row) => row.id === conversationId)
  )

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(false)
      const response = await getPortalProjectConversation(projectId)
      if (cancelled) return
      setLoading(false)
      if (!response.ok || !response.data) {
        setError(true)
        return
      }
      setSetup(response.data.customer_setup)
      if (response.data.conversation) {
        dispatch(upsertConversation(response.data.conversation))
        setConversationId(response.data.conversation.id)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [projectId, dispatch])

  useOpenConversations([conversationId])

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />

  if (error) {
    return (
      <Alert
        type={"error"}
        showIcon
        message={t("portal.messages.errorTitle", "Unable to load messages")}
        description={t(
          "portal.messages.errorBody",
          "Something went wrong while opening this conversation. Please try again."
        )}
      />
    )
  }

  // A clear state rather than an empty room: the customer is told what is
  // missing instead of being left to wonder why nobody answers.
  if (!conversation) {
    return (
      <Alert
        type={"info"}
        showIcon
        message={t(
          "portal.messages.notReadyTitle",
          "Messages are not available yet"
        )}
        description={
          setup?.missing_owner
            ? t(
                "portal.messages.noPm",
                "A project manager has not been assigned to this project yet. You will be able to message them here once they are."
              )
            : t(
                "portal.messages.notReady",
                "Your project manager has not opened a conversation yet. You will see it here when they do."
              )
        }
      />
    )
  }

  return (
    <div
      data-testid={"portal-project-messages"}
      className={
        "h-[32rem] overflow-hidden rounded-lg border border-gray-200 bg-white lg:h-[34rem]"
      }
    >
      <ConversationView conversation={conversation} portal />
    </div>
  )
}
